import { useEffect } from "react";
import { Button } from "./button";
import { Card } from "./card";

interface ConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    description: string;
    variant?: "default" | "destructive";
    confirmButtonText?: string;
    isLoading?: boolean;
    loadingText?: string;
}

export function ConfirmationModal({
    isOpen,
    onClose,
    onConfirm,
    title,
    description,
    variant = "default",
    confirmButtonText = "Confirm",
    isLoading = false,
    loadingText,
}: ConfirmationModalProps) {
    // Handle escape key
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape" && !isLoading) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
            // Prevent body scroll when modal is open
            document.body.style.overflow = "hidden";
        }

        return () => {
            document.removeEventListener("keydown", handleEscape);
            document.body.style.overflow = "unset";
        };
    }, [isOpen, onClose, isLoading]);

    // Handle backdrop click
    const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget && !isLoading) {
            onClose();
        }
    };

    if (!isOpen) return null;

    const buttonVariant = variant === "destructive" ? "destructive" : "default";
    const titleColorClass = variant === "destructive" ? "text-destructive" : "text-foreground";
    const displayLoadingText = loadingText || `${confirmButtonText}ing...`;

    return (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center sm:items-start justify-center sm:pt-20 z-50 p-4"
            onClick={handleBackdropClick}
        >
            <Card
                className="max-w-md w-full p-6 space-y-4 shadow-2xl animate-in fade-in-0 zoom-in-95 duration-200"
                style={{ backgroundColor: "hsl(var(--card))", borderColor: "hsl(var(--border))" }}
            >
                <div className="space-y-2">
                    <h3 className={`text-lg font-semibold ${titleColorClass}`}>{title}</h3>
                    <p className="text-foreground">{description}</p>
                </div>
                <div className="flex gap-2 justify-end">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={isLoading}
                        className="hover:text-primary hover:border-primary"
                    >
                        Cancel
                    </Button>
                    <Button variant={buttonVariant} onClick={onConfirm} disabled={isLoading}>
                        {isLoading ? displayLoadingText : confirmButtonText}
                    </Button>
                </div>
            </Card>
        </div>
    );
}
